// Layer 4 (UI): the hardness pair beside the stage panel (polish.P11, comp
// variant A): the measured daily feed (WIT-021) over the hourly JT-023
// hardness flag, stacked in one card sized to its content (adjudication 4 —
// no tall sparkline, no dead canvas). Figures come from `hardnessPair`.
import { useState } from 'react';
import { ChartExpanderButton, ControlChart, MeterBar, TrendChart } from '@/versions/v1/modules/_viz';
import type { hardnessPair } from './Scene.transform';
import { SynChip } from './SynChip';
import { feedSubLine, KW_TREND_HEIGHT, kwTrendDomain, meterScale, valueParts } from './HardnessPair.transform';

type Pair = ReturnType<typeof hardnessPair>;

const LABEL = 'text-[11px] font-semibold uppercase tracking-[0.07em] text-muted';

export function HardnessPair({ pair }: { pair: Pair }) {
  const feedValue = valueParts(pair.feed.value);
  const scale = meterScale(pair.feed.meter, feedValue.unit);
  const [open, setOpen] = useState<ReadonlySet<'feed' | 'kw'>>(() => new Set());
  const toggle = (key: 'feed' | 'kw') =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  return (
    <div className="card-matte-2 flex min-w-0 flex-col px-4 py-3.5 tabular-nums" data-hardness-pair>
      <div className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <p className={LABEL}>{pair.feed.label}</p>
          <ChartExpanderButton
            expanded={open.has('feed')}
            label={`${pair.feed.label} control chart`}
            onToggle={() => toggle('feed')}
          />
        </div>
        <p className="mt-1 text-2xl font-semibold tracking-tight text-ink-primary" data-pair-value="feed">
          {feedValue.figure}
          {feedValue.unit ? <span className="ml-1 text-xs font-semibold tracking-normal text-muted">{feedValue.unit}</span> : null}
        </p>
        {feedSubLine(pair.feed.sub) ? (
          <p className="mt-0.5 text-xs text-muted" data-pair-feed-sub>
            {feedSubLine(pair.feed.sub)}
          </p>
        ) : null}
        <MeterBar
          className="mt-2"
          value={pair.feed.meter.value}
          max={pair.feed.meter.max}
          band={pair.feed.meter.band}
          // The sub-line already states the design figure, so the marker's
          // caption would say it twice (K10c item 6); the tick keeps its label.
          marker={{ value: pair.feed.meter.design, label: `Design ${pair.feed.meter.design.toFixed(1)} stph` }}
          markerCaption={false}
          tone={pair.feed.meter.tone}
        />
        <p className="mt-1 flex justify-between text-[10px] text-muted" data-pair-scale>
          <span>{scale.start}</span>
          <span>{scale.band}</span>
          <span>{scale.end}</span>
        </p>
        {open.has('feed') ? (
          <ControlChart model={pair.feed.chart} className="mt-3 [&_svg]:overflow-visible" seriesTone="ink" pointMarkers="highlighted" />
        ) : null}
      </div>
      <div className="border-t border-[var(--color-divider)] pt-3">
        <div className="flex items-start justify-between gap-2">
          <p className={LABEL}>{pair.kw.label}</p>
          <span className="flex items-center gap-1.5">
            {pair.kw.trend.synthetic ? <SynChip /> : null}
            <ChartExpanderButton
              expanded={open.has('kw')}
              label={`${pair.kw.label} control chart`}
              onToggle={() => toggle('kw')}
            />
          </span>
        </div>
        <p className="mt-1 text-2xl font-semibold tracking-tight text-ink-primary" data-pair-value="kw">
          {pair.kw.value}
        </p>
        <p className="mt-0.5 text-xs text-muted">{pair.kw.sub}</p>
        <TrendChart
          className="mt-2"
          height={KW_TREND_HEIGHT}
          yDomain={kwTrendDomain(pair.kw.trend.points, pair.kw.chart.limits)}
          // The 1280 card is too narrow for every hourly tick: drop a tick
          // before it prints over the first or last (polish.P11 r2).
          avoidTickOverlap
          categories={pair.kw.trend.categories}
          series={[{ name: 'kW / tph', points: [...pair.kw.trend.points], tone: 'neutral' }]}
        />
        {open.has('kw') ? (
          <ControlChart model={pair.kw.chart} className="mt-3" seriesTone="ink" pointMarkers="highlighted" />
        ) : null}
      </div>
    </div>
  );
}
